import React from "react";
import { Link } from "react-router-dom";
import {
  Ship,
  Pickaxe,
  Truck,
  Train,
  Zap,
  Thermometer,
  Flame,
  Cloud,
  TrendingUp,
  FlaskConical,
  Fuel,
  Mail,
  ArrowRight,
} from "lucide-react";

const heroImage =
  "/Images/services/enviromental/decarbonization/descarbonization.webp";

const features = [
  {
    icon: Ship,
    title: "Sector naviero",
    description:
      "Aplicaciones de biohidrógeno para reducir emisiones y optimizar la eficiencia energética marítima.",
  },
  {
    icon: Pickaxe,
    title: "Sector minero",
    description:
      "Soluciones energéticas sostenibles para operaciones mineras de alta demanda y entornos industriales.",
  },
  {
    icon: Truck,
    title: "Logística y transporte",
    description:
      "Impulsamos sistemas de movilidad limpia para flotas de carga y transporte terrestre.",
  },
  {
    icon: Train,
    title: "Trenes y locomotoras",
    description:
      "Integración de tecnologías de hidrógeno en sistemas ferroviarios modernos y eficientes.",
  },
  {
    icon: Zap,
    title: "Generación eléctrica",
    description:
      "Producción de energía más limpia mediante soluciones basadas en biohidrógeno.",
  },
];

const benefits = [
  {
    title: "Reducción de CO₂",
    value: "15%",
    icon: Cloud,
  },
  {
    title: "Disminución de la temperatura",
    value: "10%",
    icon: Thermometer,
  },
  {
    title: "Aumento de la productividad",
    value: "10%",
    icon: TrendingUp,
  },
  {
    title: "Aumento del poder calorífico",
    value: "5%",
    icon: Flame,
  },
];

const Decarbonization = () => {
  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div className="relative h-[420px] sm:h-[520px] md:h-[680px] overflow-hidden">
          <img
            src={heroImage}
            alt="Descarbonización"
            className="w-full h-full object-cover scale-105"
          />

          <div className="absolute inset-0 bg-black/30" />

          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/20" />
          <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-green-500/10 blur-3xl rounded-full" />

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ee7d32]/15 border border-[#ee7d32]/30 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] backdrop-blur-md">
                  Servicios
                </span>

                <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                  Descarbonización
                </h1>

                <p className="mt-6 text-white/80 text-sm sm:text-base md:text-lg leading-7 max-w-2xl">
                  Energía limpia para un futuro sostenible. Starfuel Technology
                  ofrece soluciones innovadoras basadas en biohidrógeno para
                  mejorar la eficiencia energética, reducir emisiones y
                  transformar la operación industrial con mayor sostenibilidad y
                  rentabilidad.
                </p>

                <div className="mt-10 flex flex-wrap gap-4">
                  <Link
                    to="/contact-us?division=ambiental&service=Descarbonización"
                    className="inline-flex px-7 py-3 rounded-xl bg-[#ee7d32] hover:bg-[#d96d25] transition-all duration-300 text-white font-semibold shadow-lg shadow-[#ee7d32]/30"
                  >
                    Solicitar información
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ¿QUÉ ES STARFUEL? */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#ee7d32]/10 text-[#ee7d32] font-semibold text-sm uppercase tracking-wider">
                Tecnología Starfuel
              </span>

              <h2 className="mt-6 text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                ¿Qué es Starfuel?
              </h2>

              <p className="mt-6 text-lg text-gray-600 leading-8">
                Starfuel es una tecnología innovadora que incorpora
                <span className="font-semibold text-[#ee7d32]">
                  {" "}
                  12% de hidrógeno verde{" "}
                </span>
                en combustibles fósiles, mejorando la eficiencia energética y
                reduciendo las emisiones en operaciones industriales y de
                transporte.
              </p>

              <p className="mt-4 text-lg text-gray-600 leading-8">
                Permite una transición energética progresiva, aprovechando la
                infraestructura existente y facilitando la adopción de
                soluciones más eficientes sin necesidad de reemplazar equipos o
                motores.
              </p>
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-3xl bg-white border border-gray-200 p-10 shadow-xl hover:shadow-2xl transition-all duration-300">
                <img
                  src="/Images/services/enviromental/decarbonization/decarbonization_002.webp"
                  alt="Starfuel"
                  className="absolute top-0 right-0 w-40 opacity-10 pointer-events-none select-none"
                />
                <div className="relative z-10">
                  <div className="w-16 h-16 rounded-2xl bg-[#ee7d32]/10 flex items-center justify-center mb-6">
                    <Zap className="w-8 h-8" stroke="#ee7d32" />
                  </div>

                  <h3 className="text-3xl font-bold text-gray-900">
                    Eficiencia operativa
                  </h3>

                  <p className="mt-4 text-gray-600 leading-7">
                    Starfuel extiende la vida útil de los equipos, mejora la
                    eficiencia económica y reduce costos operativos, siendo una
                    alternativa viable sin necesidad de reconversión
                    tecnológica.
                  </p>

                  <div className="mt-6 p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-sm text-gray-700 leading-6">
                      <span className="font-semibold text-[#ee7d32]">
                        Resultado:
                      </span>{" "}
                      Mayor eficiencia con implementación inmediata en
                      operaciones reales.
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-[#ee7d32]/10 blur-3xl rounded-full -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* INTEGRACIÓN EN SECTORES CLAVE */}
      <section className="relative py-24 bg-white overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="text-4xl sm:text-5xl md:text-5xl font-bold text-[#111] leading-none">
              Integración en{" "}
              <span className="text-[#ee7d32]">Sectores Clave</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 py-6 sm:grid-cols-2 xl:grid-cols-5 gap-6">
            {features.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-8 pt-14 text-center shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(238,125,50,0.18)] transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-[#ee7d32] rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8" stroke="#fff" strokeWidth={2.2} />
                  </div>

                  <div className="h-20 flex items-center justify-center">
                    <h3 className="text-[20px] font-[800] text-[#111] leading-tight">
                      {item.title}
                    </h3>
                  </div>

                  <div className="mt-5 mx-auto w-14 h-[3px] bg-[#ee7d32] rounded-full group-hover:w-24 transition-all duration-300" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFICIOS */}
      <section className="relative py-24 bg-[#f8f7f4] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-[3px] bg-[#ee7d32] rounded-full" />
              <span className="text-xs font-semibold uppercase tracking-[4px] text-[#ee7d32]">
                Beneficios
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-[#111] leading-[1.05]">
              Mayor eficiencia con
              <span className="block text-[#ee7d32] mt-2">
                menores emisiones
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {benefits.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="group relative flex flex-col justify-between bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#ee7d32]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6" stroke="#ee7d32" />
                    </div>

                    <div className="text-3xl font-bold text-[#111] tracking-tight">
                      {item.value}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm text-gray-600 leading-5">
                      {item.title}
                    </p>

                    <div className="w-0 group-hover:w-10 h-[2px] bg-[#ee7d32] transition-all duration-300 rounded-full" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* MODO DE USO (MEZCLA) */}
          <div className="mt-10">
            <div className="relative w-full bg-white rounded-3xl p-6 md:p-10 overflow-hidden shadow-md border border-gray-100">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] bg-[#ee7d32]/5 rounded-full blur-[120px]" />
              <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-12">
                <div className="flex items-start gap-5 max-w-xl">
                  <div className="w-14 h-14 rounded-2xl  flex items-center justify-center">
                    <FlaskConical
                      className="w-10 h-10 text-[#ee7d32]"
                      stroke="#ee7d32"
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-[2px] bg-[#ee7d32] rounded-full" />
                      <span className="text-xs font-semibold uppercase tracking-[3px] text-[#ee7d32]">
                        Dosificación
                      </span>
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">
                      Modo de uso (Mezcla)
                    </h3>

                    <p className="text-sm md:text-base text-gray-600 leading-6">
                      <span className="font-semibold text-gray-900">
                        1:1000
                      </span>{" "}
                      — Por cada{" "}
                      <span className="font-medium">1000 litros</span>, se
                      activa{" "}
                      <span className="font-medium text-[#ee7d32]">
                        1 litro concentrado
                      </span>{" "}
                      de Starfuel Technology.
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="flex items-center gap-6">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-2xl bg-[#ee7d32]/10 border border-[#ee7d32]/20 flex items-center justify-center shadow-sm">
                        <FlaskConical
                          className="w-6 h-6 text-[#ee7d32]"
                          stroke="#ee7d32"
                        />
                      </div>
                      <span className="text-xs text-gray-500">1L activo</span>
                    </div>

                    <div className="relative w-12 h-[2px] bg-[#ee7d32]">
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 rotate-45 bg-[#ee7d32]" />
                      <div className="absolute left-1/2 top-1/2 -translate-y-1/2 w-1 h-1 bg-[#ee7d32] rounded-full animate-pulse" />
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 border border-gray-200 flex items-center justify-center">
                        <Fuel className="w-6 h-6 text-gray-500" />
                      </div>
                      <span className="text-xs text-gray-500">
                        1000L optimizados
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA DESCARBONIZACIÓN */}
      <section className="relative py-10 md:py-10 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[30px] border border-[#ececec] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-[#ee7d32]/50 to-transparent" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 px-6 py-8 md:px-10 md:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-6 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />
                  <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center">
                    <Mail className="w-10 h-10 stroke-[#ee7d32]" />
                  </div>
                </div>

                <div>
                  <span className="inline-flex items-center rounded-full bg-[#ee7d32]/10 border border-[#ee7d32]/20 px-3 py-1 text-[11px] md:text-[12px] font-semibold uppercase tracking-widest text-[#ee7d32]">
                    Servicio de descarbonización
                  </span>

                  <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-[#111] leading-tight">
                    Reduce las emisiones de tu operación industrial
                  </h3>

                  <p className="mt-4 text-sm md:text-base leading-7 text-[#6b7280] max-w-2xl">
                    Descubre cómo Starfuel Technology puede ayudarte a alcanzar
                    tus objetivos de descarbonización y mejorar la eficiencia de
                    tu combustible desde el primer uso.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=ambiental&service=Descarbonización"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Solicitar asesoría
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Decarbonization;
